import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { fetchOrderDetailV2 } from 'utils/api'
import type {
  OrderV2OrderDetailResponseType,
  OrderV2PicType,
  OrderProductsDataType,
  OrderV2HubType,
} from 'pages/Order/components/Detail/mapOrderProducts'
import { getDriverHistory } from 'utils/apiList/externalDriver'
import { fetchOrderProducts } from 'features/Order/services/order'
import type { ActivityPicType, BuyerType, StaffType } from './orderDetailSlice'
import {
  SLICE_NAME,
  setOrderDetail,
  setOrderProducts,
  setOrderEvents,
  setOrderPic,
  setCustomerAddressImageUrls,
  setBuyerInfo,
  setDriverHistory,
} from './orderDetailSlice'

type AddressImageUrlsResponseType = {
  delivery?: {
    destination?: {
      address_image_urls?: string[]
    }
  }
}

export const getAddressImageUrls = (data: AddressImageUrlsResponseType): string[] =>
  data.delivery?.destination?.address_image_urls ?? []

export const mapBuyerFromResponse = ({
  customer,
}: Pick<OrderV2OrderDetailResponseType, 'customer'>): BuyerType | undefined =>
  customer ? { name: customer.name, phone: customer.contact } : undefined

const hasPicStaff = (staff?: { id: number; name: string }) => !!staff && !!staff.name

const mapPicStaff = (role: StaffType['role'], name: string): StaffType => ({
  role,
  name,
  phone: '',
  externalDriverPartner: '',
})

export const mapOrderPicFromResponse = ({
  hub,
  pic,
}: Pick<OrderV2OrderDetailResponseType, 'hub'> & {
  pic?: OrderV2PicType
}): ActivityPicType[] => {
  const picData = pic
  if (!picData) return []

  const staff: StaffType[] = []

  if (hasPicStaff(picData.picker)) {
    staff.push(mapPicStaff('PICKER', picData.picker?.name || ''))
  }

  if (hasPicStaff(picData.packer)) {
    staff.push(mapPicStaff('PACKER', picData.packer?.name || ''))
  }

  if (hasPicStaff(picData.courier)) {
    staff.push({
      role: 'DRIVER',
      name: picData.courier?.name || '',
      phone: picData.courier?.contact || '',
      externalDriverPartner:
        picData.courier?.courier_type === 'EXTERNAL_3PL' ? picData.courier?.provider || '' : '',
    })
  }

  if (staff.length <= 0) return []

  return [
    {
      hub: hub?.name || '',
      hubType: hub?.type === 'HUB' ? 'HUB' : 'MEGAHUB',
      staff,
    },
  ]
}

export const getOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/getOrderDetail`,
  async (orderId: string, { rejectWithValue, dispatch, fulfillWithValue, getState }) => {
    try {
      const { data } = await fetchOrderDetailV2(orderId)
      const currentAddressImageUrls =
        (getState() as StoreStateType).orderDetail.orderDetail.customer.addressImageUrls ?? []
      const addressImageUrls = getAddressImageUrls(data)
      dispatch(
        setOrderDetail({
          ...data,
          // Buyer is populated by OMS, which may finish before this legacy request.
          buyer: (getState() as StoreStateType).orderDetail.orderDetail.buyer,
          customer: {
            ...data.customer,
            addressImageUrls:
              addressImageUrls.length > 0 ? addressImageUrls : currentAddressImageUrls,
          },
        }),
      )
      return fulfillWithValue(data)
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const getOrderProducts = createAsyncThunk(
  `${SLICE_NAME}/getOrderProducts`,
  async (orderId: number, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const { data } = await fetchOrderProducts(orderId)
      const hubs: OrderV2HubType[] = []

      if (data?.hub) {
        hubs.push({
          id: String(data.hub.id || ''),
          type: String(data.hub.type || ''),
          name: String(data.hub.name || ''),
        })
      }

      const orderProductsData: OrderProductsDataType = {
        orderId: Number(data?.order?.id) || orderId,
        hub: hubs,
        rerouteFromName: data.reroute_from?.name?.trim() || '',
        items: data?.fulfillment ? data.fulfillment.flatMap((f) => f.items || []) : [],
      }

      dispatch(setOrderProducts(orderProductsData))
      dispatch(setOrderPic(mapOrderPicFromResponse(data)))
      dispatch(setCustomerAddressImageUrls(getAddressImageUrls(data)))
      dispatch(setBuyerInfo(mapBuyerFromResponse(data)))
      const sortedEvents = [...(data.events || [])].sort(
        (a, b) => new Date(b.occurred_time_ts).getTime() - new Date(a.occurred_time_ts).getTime(),
      )
      dispatch(setOrderEvents(sortedEvents))
      return fulfillWithValue(orderProductsData)
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const getExternalDriverHistory = createAsyncThunk(
  `${SLICE_NAME}/getExternalDriverHistory`,
  async (orderID: number, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const {
        data: { data: result },
      } = await getDriverHistory({ orderID })
      dispatch(setDriverHistory(result))
      return fulfillWithValue(result)
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
